import axios from "axios";

const ENV_URL = new URL(process.env.REACT_APP_BACKEND_URL);
const bareHost = (h) => h.replace(/^www\./, "");
// Same site reached via www or bare domain: call the API on the current origin so cookies and CORS stay same-origin.
const BASE = bareHost(ENV_URL.hostname) === bareHost(window.location.hostname) ? window.location.origin : ENV_URL.origin;

export const API = `${BASE}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

const RKEY = "mwjk-rsec";
let reportSec = localStorage.getItem(RKEY) === "gc" ? "gc" : "mwjk";
export const getReportSec = () => reportSec;
export const setReportSec = (s) => {
  if (s === reportSec || !["gc", "mwjk"].includes(s)) return;
  reportSec = s;
  localStorage.setItem(RKEY, s);
  window.dispatchEvent(new Event("mwjk-rsec"));
};
const SEC_URL = /^\/?(settings$|finance\/(config|period\/)|collections\/[\w-]+\/goals$|signatures\/)/;
api.interceptors.request.use((cfg) => {
  if (SEC_URL.test(cfg.url || "") && !(cfg.params && cfg.params.sec)) cfg.params = { ...(cfg.params || {}), sec: reportSec };
  return cfg;
});

let refreshing = null;
const CKEY = "mwjk-get-cache";
const NO_GET_CACHE = /\/(export|files|messages\/files|auth\/(login|refresh|logout))/;
const cacheKey = (cfg) => `${cfg.url}?${new URLSearchParams(cfg.params || {}).toString()}`;
const readCache = () => { try { return JSON.parse(localStorage.getItem(CKEY) || "{}"); } catch { return {}; } };
const getCache = (cfg) => readCache()[cacheKey(cfg)];
const putCache = (r) => {
  const cfg = r.config || {};
  if ((cfg.method || "get").toLowerCase() !== "get" || cfg.responseType === "blob" || NO_GET_CACHE.test(cfg.url || "")) return;
  const all = readCache();
  all[cacheKey(cfg)] = r.data;
  try { localStorage.setItem(CKEY, JSON.stringify(all)); } catch { localStorage.removeItem(CKEY); }
};
api.interceptors.response.use((r) => { putCache(r); return r; });
const QKEY = "mwjk-offline-queue";
const OFFLINE_RE = /^\/?(tithes|donations|progress|registrations)(\/[\w-]+)?$/;
export const offlineQueue = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const saveQueue = (q) => { localStorage.setItem(QKEY, JSON.stringify(q)); window.dispatchEvent(new Event("mwjk-queue")); };

export function pendingRows(endpoint, churches = [], match = () => true) {
  return offlineQueue()
    .filter((i) => i.method === "post" && (i.url || "").replace(/^\//, "") === endpoint && match(i.data || {}))
    .map((i) => ({ ...i.data, id: i.qid || `offline-${i.at}`, _pending: true, created_at: i.at, church_name: churches.find((c) => c.id === i.data?.church_id)?.name || i.data?.church_name || "" }));
}

let flushing = null;
async function flushNow() {
  const q = offlineQueue();
  let sent = 0;
  while (q.length && navigator.onLine) {
    const item = q[0];
    try { await api.request({ method: item.method, url: item.url, data: item.data, _noQueue: true }); } catch (e) { if (!e.response) break; }
    q.shift();
    saveQueue(q);
    sent += 1;
  }
  return sent;
}

export function flushOffline() {
  if (flushing) return flushing.then(() => 0);
  const run = navigator.locks?.request ? () => navigator.locks.request("mwjk-offline-flush", flushNow) : flushNow;
  flushing = run().finally(() => { flushing = null; });
  return flushing;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const method = (cfg.method || "").toLowerCase();
    if (!err.response && method === "get") {
      const hit = getCache(cfg);
      if (hit !== undefined) return { data: hit, status: 200, config: cfg, headers: {}, fromCache: true };
    }
    if (!err.response && !cfg._noQueue && ["post", "put"].includes(method) && OFFLINE_RE.test(cfg.url || "") && !(cfg.data instanceof FormData)) {
      const data = typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data;
      const qid = `offline-${Date.now()}`;
      saveQueue([...offlineQueue(), { method, url: cfg.url, data, at: new Date().toISOString(), qid }]);
      return { data: { ...data, id: qid, offline: true }, status: 202, config: cfg, headers: {} };
    }
    return Promise.reject(err);
  }
);
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    if (err.response?.status === 401 && !cfg._retry && !url.includes("/auth/login") && !url.includes("/auth/refresh")) {
      cfg._retry = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
      try {
        await refreshing;
        return api(cfg);
      } catch (e) {
        window.dispatchEvent(new Event("mwjk-logout"));
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d && typeof d.msg === "string" ? d.msg.replace(/^Value error, /, "") : JSON.stringify(d))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fileUrl = (id) => `${API}/files/${id}`;
